import { useEffect, useState } from 'react';
import { CirclePlus, X } from 'lucide-react';
import CustomLabels from '../../../components/CustomLabels';
import CardsGroup from '../../../components/dynamicFieldCards/CardsGroupForEvent';

import NewEventDayService from '../../../services/event-day-service';
import NewEventAgendaService from '../../../services/event-agenda-service';

// Step3Form for managing event dates and agendas (previously Step2Form)
const Step3 = ({ register, errors, setValue, watch, getValues }) => {
  const [eventDays, setEventDays] = useState([]);
  
  // Watch for any changes to the 'dates' field
  const watchEventDays = watch("eventDays");
  
  // Definition of agenda fields
  const agendaFields = [
    { name: 'timeRange', label: '時間' },
    { name: 'programme', label: '議程' },
    { name: 'speakers', label: '講者' }
  ];
  
  // Initialize the dates array if it doesn't exist
  useEffect(() => {
    // if (!watchEventDays || watchEventDays.length === 0) {
    //   setValue("eventDays", [
    //     { 
    //       date: "", 
    //       agendas: [{ timeRange: "", programme: "", speakers: "" }] 
    //     }
    //   ]);
    // } else {
      setEventDays(watchEventDays);
    // }
  }, [setValue, watchEventDays]);

  // Add a new date
  const addDate = () => {
    const updatedEventDays = [...getValues("eventDays"), { 
      date: "", 
      agendas: [{ timeRange: "", programme: "", speakers: "" }] 
    }];
    setValue("eventDays", updatedEventDays);
    setEventDays(updatedEventDays);
  };

  // Remove a date
  const removeDate = async (id, dateIndex) => {
    try {
      if (id) {
        const res = await NewEventDayService.delete(id);
      }
      const updatedEventDays = getValues("eventDays").filter((_, index) => index !== dateIndex);
      setValue("eventDays", updatedEventDays);
      setEventDays(updatedEventDays);  
    } catch (error) {
      console.error("error: ", error);
    }
  };

  // This function will be used to update the agendas array for a specific date
  const updateAgendas = (dateIndex, newAgendas) => {
    const updatedEventDays = [...getValues("eventDays")];
    updatedEventDays[dateIndex] = {
      ...updatedEventDays[dateIndex],
      agendas: newAgendas
    };
    setValue("eventDays", updatedEventDays);
    setEventDays(updatedEventDays);
  };

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="日期與議程" />
        {watchEventDays && watchEventDays.map((dateItem, dateIndex) => (
          <div key={dateIndex}>
            {/* Add a divider if this is not the first date */}
            {dateIndex > 0 && (
              <div className="border-t border-gray-200 my-6"></div>
            )}
            
            <div className="mb-6 p-4 border rounded border-gray-300 relative">
              <button
                type="button"
                onClick={() => removeDate(dateItem?.id, dateIndex)}
                className="border border-red-500 bg-red-500 text-white hover:bg-red-600 rounded-full p-1 absolute -top-3 end-3 font-bold"
              >
                <X size={18} />
              </button>
                            
              <div className="mb-4">
                <label className="block text-sm font-medium">日期名稱
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  {...register(`eventDays.${dateIndex}.date`, {
                    required: '此欄位為必填'
                  })}
                  className={`w-full p-2 border rounded ${
                    errors?.eventDays?.[dateIndex]?.date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors?.eventDays?.[dateIndex]?.date && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.eventDays[dateIndex].date.message}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium">議程</label>
                
                {/* Custom CardsGroup wrapper component for agendas */}
                <AgendaCardsGroup
                  register={register}
                  errors={errors}
                  setValue={(name, value) => {
                    // This intercepts the setValue call from CardsGroup
                    // and updates only the agendas for this specific date
                    updateAgendas(dateIndex, value);
                  }}
                  watch={(name) => {
                    // Return only the agendas for this specific date
                    return dateItem.agendas;
                  }}
                  dateIndex={dateIndex}
                  fields={agendaFields}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add date button placed after the last date card */}
        <button
          type="button"
          onClick={addDate}
          className="my-auto p-2 w-fit h-fit flex items-center text-gray-500 hover:text-gray-700"
        >
          <CirclePlus size={32} strokeWidth={1} className="mr-1" />
          <span className="text-sm">新增日期</span>
        </button>
      </div>
    </div>
  );
};

// This is a custom wrapper component that adapts CardsGroup to work with agenda items
const AgendaCardsGroup = ({ register, errors, setValue, watch, dateIndex, fields }) => {
  // Get the agendas from the watch function (which returns dateItem.agendas)
  const agendas = watch("agendas") || [];

  // Register helper to prefix all field paths with the correct dateIndex
  const registerWithPrefix = (name, options) => {
    return register(`eventDays.${dateIndex}.${name}`, options);
  };

  // Error helper to navigate to the errors with the right prefix
  const getErrors = () => {
    return errors?.eventDays?.[dateIndex] || {};
  };

  // Custom register function that adds required validation to all agenda fields
  const registerWithValidation = (name, options) => {
    return registerWithPrefix(name, {
      ...options,
      required: '此欄位為必填'
    });
  };

  // 刪除 agenda 項目的方法
  const deleteAgenda = async (agendaId, eventDayId) => {
    try {
      if (agendaId) {
        const res = await NewEventAgendaService.delete(agendaId);
      }
      return true;
    } catch (error) {
      console.error("Failed to delete agenda:", error);
      throw error; // 向上拋出錯誤，讓 CardsGroup 可以處理
    }
  };

  // 從 watch 中獲取當前日期項目的 ID
  const currentEventDay = watch(`eventDays.${dateIndex}`) || {};
  const eventDayId = currentEventDay.id;

  return (
    <CardsGroup
      register={(name, options) => registerWithValidation(name, options)}
      errors={getErrors()}
      setValue={setValue}
      watch={watch}
      groupName="agendas"
      fields={fields}
      fieldTitle="agendas"
      userCanAdd={true}
      onDeleteItem={deleteAgenda} // 添加刪除方法
      parentId={eventDayId} // 傳遞父級 ID (eventDayId)
    />
  );
};

export default Step3;